import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Menu, X, Phone, Globe } from 'lucide-react';
import { Button } from './ui/button';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_rade-gradnja/artifacts/zu4djkks_Logo%20RadeGradnja%20i%20Renovacije.png";

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { t, language, toggleLanguage } = useLanguage();
  const location = useLocation();

  const navLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/usluge', label: t('nav.services') },
    { path: '/galerija', label: t('nav.gallery') },
    { path: '/kontakt', label: t('nav.contact') }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-sm border-b border-gray-100" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-24">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="navbar-logo">
            <img 
              src={LOGO_URL} 
              alt="Rade Gradnja Logo" 
              className="h-16 w-auto drop-shadow-md"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6 lg:gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.path.replace('/', '') || 'home'}`}
                className={`font-medium text-sm uppercase tracking-wider transition-colors duration-200 whitespace-nowrap ${
                  isActive(link.path)
                    ? 'text-[#D4AF37]'
                    : 'text-[#0B1E3B] hover:text-[#D4AF37]'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right side - Language & Phone */}
          <div className="hidden md:flex items-center space-x-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleLanguage}
              className="flex items-center gap-2 text-[#0B1E3B] hover:text-[#D4AF37]"
              data-testid="language-toggle"
              aria-label="Toggle language"
            >
              <Globe className="h-4 w-4" />
              <span className="uppercase font-medium">{language === 'sr' ? 'EN' : 'SR'}</span>
            </Button>
            
            <a 
              href="tel:+381631627662" 
              className="flex items-center gap-2 bg-[#0B1E3B] text-white px-4 py-2 rounded-md hover:bg-[#D4AF37] hover:text-[#0B1E3B] transition-colors duration-200"
              data-testid="navbar-phone"
            >
              <Phone className="h-4 w-4" />
              <span className="font-medium">+381 63 162 7662</span>
            </a>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleLanguage}
              className="text-[#0B1E3B]"
              data-testid="mobile-language-toggle"
            >
              <Globe className="h-5 w-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsOpen(!isOpen)}
              className="text-[#0B1E3B]"
              data-testid="mobile-menu-button"
              aria-label="Toggle menu"
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden py-4 border-t border-gray-100 animate-fade-in" data-testid="mobile-menu">
            <div className="flex flex-col space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  data-testid={`mobile-nav-link-${link.path.replace('/', '') || 'home'}`}
                  className={`font-medium text-sm uppercase tracking-wider py-2 ${
                    isActive(link.path)
                      ? 'text-[#D4AF37]'
                      : 'text-[#0B1E3B]'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <a 
                href="tel:+381631627662" 
                className="flex items-center justify-center gap-2 bg-[#0B1E3B] text-white px-4 py-3 rounded-md mt-4"
                data-testid="mobile-phone"
              >
                <Phone className="h-4 w-4" />
                <span className="font-medium">+381 63 162 7662</span>
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
